import React from 'react';
import { Banner, Button } from '@wikifactory/voxel-ui';
import { observer } from 'mobx-react';
import { StatusType } from 'libs/gutClient/types';
import { AlertIcon } from 'icons';

import Icon from 'components/Icon';

import { KeepType, useResolveConflict } from 'hooks/cad/useResolveConflict';
import { SyncRepoAppManager } from '../appManager';

type LocalChangesBannerProps = {
  syncRepoAppManager: SyncRepoAppManager;
};

export const LocalChangesBanner = observer(
  ({ syncRepoAppManager }: LocalChangesBannerProps) => {
    const { contributingManager } = syncRepoAppManager;

    const hasConflicts =
      syncRepoAppManager.selectableLocalChangesTableRows.some(localChange => {
        return localChange.type === StatusType.Conflicted;
      });

    const { confirmModal, openConfirmModal, closeConfirmModal } =
      useResolveConflict({
        numBulkFiles: syncRepoAppManager.localChanges.reduce(
          (counter, localChange) =>
            counter + (localChange.type === StatusType.Conflicted ? 1 : 0),
          0,
        ),
        onKeepCloud: async () => {
          await syncRepoAppManager.resolveAllConflictKeepCloud();
          closeConfirmModal();
        },
        onKeepLocal: async () => {
          await syncRepoAppManager.resolveAllConflictKeepLocal();
          closeConfirmModal();
        },
      });

    let banner = null;
    if (
      contributingManager.canResumeContribution &&
      !contributingManager.isContributing
    ) {
      const { pendingContributionState } = contributingManager;
      const { title /* , description */ } = pendingContributionState;

      let numAdditions = 0;
      let numModifications = 0;
      let numDeleted = 0;
      let completedUploads = 0;

      pendingContributionState.items.forEach(item => {
        if (item.type === StatusType.Added) {
          numAdditions += 1;
        } else if (item.type === StatusType.Modified) {
          numModifications += 1;
        } else if (item.type === StatusType.Deleted) {
          numDeleted += 1;
        }
        if (item.uploadedHash) completedUploads += 1;
        if (item.fileId && !item.signedUrl) completedUploads += 1;
      });

      const neededUploads = numAdditions + numModifications;

      let completionStr = '';
      if (neededUploads > 0) {
        if (completedUploads > neededUploads) {
          completionStr = '';
        } else {
          completionStr = ` (${completedUploads}/${neededUploads} uploads completed)`;
        }
      }

      const additions = numAdditions > 0 ? `${numAdditions} Added` : '';
      const modified =
        numModifications > 0 ? `${numModifications} Modified` : '';
      const deleted = numDeleted > 0 ? `${numDeleted} Deleted` : '';
      const specs = [additions, modified, deleted].filter(Boolean).join(', ');
      banner = (
        <Banner className='mt-1' key='pullBanner' variant='error'>
          <div className='flex flex-col text-black text-14 mb-3'>
            {contributingManager.contributionErrorBannerMessage && (
              <p className='mb-1'>
                {contributingManager.contributionErrorBannerMessage}
              </p>
            )}
            <p className='mb-1'>
              Your last contribution did not finish{completionStr}. Do you want
              to resume it?
            </p>
            <p className='ml-5'>
              <b>Title: </b>
              {title} (File changes: {specs})
              {/* {description && ` - ${description}`} */}
            </p>
          </div>
          <div className='flex gap-3 self-end'>
            <Button
              primaryColor='black-hollow'
              size='small'
              variant='outlined'
              onClick={() => {
                contributingManager.CancelPendingContribution();
              }}
              endIcon={<Icon name='close' size='small' />}
            >
              <span className='text-12'>Cancel</span>
            </Button>
            <Button
              primaryColor='blue-hollow'
              size='small'
              variant='outlined'
              onClick={() => {
                contributingManager.Resume();
              }}
              endIcon={<Icon name='play' size='small' />}
            >
              <span className='text-12'>Resume</span>
            </Button>
          </div>
        </Banner>
      );
    } else if (hasConflicts) {
      banner = (
        <div className='flex mt-4 text-sm text-black cursor-default'>
          {confirmModal}
          <AlertIcon className=' mr-2' />
          <div>
            {contributingManager.contributionErrorBannerMessage && (
              <p className='mb-1'>
                {contributingManager.contributionErrorBannerMessage}
              </p>
            )}
            To be able to contribute conflicting local changes, please resolve
            them below.
            <div>
              You can: 1) Keep{' '}
              <span
                onClick={() => {
                  openConfirmModal(KeepType.Cloud);
                }}
                className='underline cursor-pointer hover:text-wfBlue'
              >
                all changes from Cloud
              </span>
              , 2) Keep{' '}
              <span
                onClick={() => {
                  openConfirmModal(KeepType.Local);
                }}
                className='underline cursor-pointer hover:text-wfBlue'
              >
                all from Local
              </span>
              , 3) select on a per file basis
            </div>
          </div>
        </div>
      );
    } else if (contributingManager.contributionErrorBannerMessage) {
      const whichTypes =
        syncRepoAppManager.repoManager?.parent?.project?.inSpace?.whichTypes;
      const canContribute = whichTypes?.includes('Contribution');

      let bannerText = '';
      let bannerVariant = 'warning';
      if (!canContribute) {
        bannerVariant = 'info';
        bannerText =
          'You have been invited to this project as a Viewer so you are not able to contribute.';
      } else if (contributingManager.isContributing) {
        bannerVariant = 'info';
        bannerText = 'Your contribution is in progress...';
      } else if (contributingManager.contributionErrorBannerMessage) {
        bannerVariant = 'error';
        bannerText = contributingManager.contributionErrorBannerMessage;
      }

      banner = (
        <Banner className='mt-8' text={bannerText} variant={bannerVariant} />
      );
    } else if (
      syncRepoAppManager.localChanges.length > 0 &&
      syncRepoAppManager.headHash === null
    ) {
      banner = (
        <Banner
          className='mt-8'
          text='Contribute your local changes so they are uploaded to the cloud repository.'
          variant='info'
        />
      );
    }
    // return <DelayedMountAndUnmount>{banner}</DelayedMountAndUnmount>;
    return banner;
  },
);
